import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import type { DeliveryNote } from '../../pages/Warehouse/types'
import { normaliseTypedPostcode } from '../../lib/ukPostcode'

// job_billing_variables() (20261002b): the billing inputs of the job, with every value that could not
// be obtained as null + an entry in missing[] (reason + how to get it). Never 0, never guessed.
interface MissingItem { key: string; scope: string; reason: string; how_to: string }
interface BillingVars {
  billing_party: { id: string; code: string; name: string } | null
  containers: { container_no: string | null; container_type: string | null; pallets: number | null; cartons: number | null }[]
  container_qty: number | null
  hbl_qty: number | null
  declaration_qty: number | null
  headings: { declaration: string | null; hs_count: number | null }[]
  deliveries: unknown[]
  warehouse_in: string | null
  customs_cleared: string | null
  warehouse_out: string | null
  warehouse_out_partial: boolean | null
  storage_days: number | null
  storage_days_unreleased: number | null
  storage_days_released: number | null
  storage_in_progress: boolean | null
  is_dg: boolean | null
  pod: { code: string; name: string } | null
  missing: MissingItem[]
}

// Read-only "what actually happened on this job" panel for the Charges tab (Gary, 2026-09-29):
// the raw billing events -- deliveries/collections raised and customs declarations filed -- so
// whoever invoices can check "3 deliveries x rate" against reality, and so a later auto-invoice
// has its inputs in one place. Computed live from the source tables on every mount, no caching.
// Blocks with no data are omitted entirely (no "0 declarations").
//
// Deliberately NOT shown: duty and import-VAT amounts. This app never receives them -- the CDS file
// carries customs VALUE only and HMRC's assessment (DMSTAX) is not captured anywhere yet -- and a
// made-up or £0 figure on a billing-check screen is worse than none. See ecustoms_submissions
// (20260911_z73_16) for the same reasoning.

interface Submission {
  id: string
  batch_id: string | null
  submission_type: string
  pva_used: boolean | null
  status: string
  reference: string | null
  mrn: string | null
  declaration_date: string | null
  submitted_at: string | null
  total_customs_value: number | null
  currency: string | null
  customs_broker_fee: number | null
}

const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit' }) : '—'
const fmtNum = (n: number) => n.toLocaleString('en-GB', { maximumFractionDigits: 2 })
const gbp = (n: number) => `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// Which channel took the goods: self/supplier pickup are their own channels; a courier/haulier note
// is grouped by the carrier's name (Release To), e.g. DPD vs Palletways; our own van = CYUKBHX.
function channelOf(n: DeliveryNote): string {
  if (n.note_type === 'COLLECTION_SELF') return 'Customer collection'
  if (n.note_type === 'COLLECTION_SUPPLIER') return 'Supplier collection'
  const to = (n.release_to_name || '').trim()
  if (/^CYUKBHX/i.test(to)) return 'CY UK own delivery'
  return to || 'Unspecified'
}

function statusOf(n: DeliveryNote): { label: string; cls: string } {
  const cd = n.fulfilment_type === 'COLLECTION_AND_DELIVERY'
  if (n.lifecycle_status === 'COMPLETED') {
    return cd
      ? { label: `Delivered ${fmtDate(n.delivered_at)}`, cls: 'text-green-700 dark:text-green-400' }
      : { label: `Collected ${fmtDate(n.collected_at || n.completed_at)}`, cls: 'text-green-700 dark:text-green-400' }
  }
  if (n.transit_status === 'IN_TRANSIT') return { label: `In transit (left ${fmtDate(n.collected_at)})`, cls: 'text-orange-600 dark:text-orange-400' }
  return { label: `Planned ${fmtDate(n.scheduled_date)}`, cls: 'text-blue-600 dark:text-blue-400' }
}

// Charge codes the hints may point at. Each is only shown if it really exists (and is active) in
// charge_codes -- a hint naming a code staff can't find is worse than no hint.
const DELIVERY_CODES = ['DELIV', 'DELIVERY_LOCAL', 'DELIVERY_NATIONAL']
const CUSTOMS_CODES = ['CUSTO']

const Hint = ({ children }: { children: React.ReactNode }) => (
  <span className="ml-2 text-[10px] text-gray-400 dark:text-gray-500 font-normal">→ {children}</span>
)

export default function BillingBasisPanel({ jobId }: { jobId: string }) {
  const [notes, setNotes] = useState<DeliveryNote[]>([])
  const [subs, setSubs] = useState<Submission[]>([])
  const [hsBySub, setHsBySub] = useState<Record<string, number>>({})
  const [existingCodes, setExistingCodes] = useState<Set<string>>(new Set())
  const [loaded, setLoaded] = useState(false)
  const [vars, setVars] = useState<BillingVars | null>(null)
  const [varsError, setVarsError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [editingPc, setEditingPc] = useState<{ id: string; value: string } | null>(null)
  const [pcError, setPcError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      supabase.from('charge_codes').select('code').eq('is_active', true).in('code', [...DELIVERY_CODES, ...CUSTOMS_CODES])
        .then(({ data }) => { if (!cancelled) setExistingCodes(new Set(((data as { code: string }[] | null) || []).map(r => r.code))) })
      const { data: job } = await supabase.from('jobs').select('job_ref').eq('id', jobId).maybeSingle()
      const ref = (job as { job_ref: string } | null)?.job_ref
      // job_id on delivery_notes is best-effort (looked up by job_ref at creation), so match on both.
      const noteQuery = supabase.from('delivery_notes').select('*').eq('status', 'ACTIVE')
        .neq('lifecycle_status', 'DRAFT').order('scheduled_date')
      const [{ data: n }, { data: s }] = await Promise.all([
        ref ? noteQuery.or(`job_id.eq.${jobId},job_ref.eq.${ref}`) : noteQuery.eq('job_id', jobId),
        supabase.from('ecustoms_submissions')
          .select('id, batch_id, submission_type, pva_used, status, reference, mrn, declaration_date, submitted_at, total_customs_value, currency, customs_broker_fee')
          .eq('job_id', jobId).in('status', ['submitted', 'accepted']).order('submitted_at', { ascending: false }),
      ])
      if (cancelled) return
      // A "Confirm & Generate" click produces a PVA + non-PVA pair under one batch_id; only what was
      // actually submitted counts, and one declaration per batch even if both variants were filed.
      const seen = new Set<string>()
      const subRows = ((s as Submission[] | null) || []).filter(r => {
        if (!r.batch_id) return true
        if (seen.has(r.batch_id)) return false
        seen.add(r.batch_id); return true
      })
      setNotes((n as DeliveryNote[] | null) || [])
      setSubs(subRows)
      if (subRows.length) {
        const { data: lines } = await supabase.from('ecustoms_submission_lines')
          .select('submission_id, commodity_code').in('submission_id', subRows.map(r => r.id))
        if (cancelled) return
        const codes: Record<string, Set<string>> = {}
        for (const l of (lines as { submission_id: string; commodity_code: string | null }[] | null) || []) {
          if (l.commodity_code) (codes[l.submission_id] ??= new Set()).add(l.commodity_code)
        }
        setHsBySub(Object.fromEntries(Object.entries(codes).map(([k, v]) => [k, v.size])))
      }
      setLoaded(true)
    })()
    return () => { cancelled = true }
  }, [jobId])

  useEffect(() => {
    let cancelled = false
    supabase.rpc('job_billing_variables', { p_job_id: jobId }).then(({ data, error }) => {
      if (cancelled) return
      if (error) { setVars(null); setVarsError(error.message); return }
      setVars(data as BillingVars); setVarsError(null)
    })
    return () => { cancelled = true }
  }, [jobId, reloadKey])

  async function savePostcode(noteId: string, raw: string) {
    const pc = normaliseTypedPostcode(raw)
    if (pc === undefined) { setPcError('Not a valid UK postcode (e.g. B76 1AH, or just B76).'); return }
    const { error } = await supabase.from('delivery_notes').update({ delivery_postcode: pc }).eq('id', noteId)
    if (error) { setPcError(error.message); return }
    setPcError(null); setEditingPc(null)
    setNotes(ns => ns.map(n => n.id === noteId ? { ...n, delivery_postcode: pc } : n))
    setReloadKey(k => k + 1)
  }

  const totals = useMemo(() => ({
    plts: notes.reduce((a, n) => a + (n.pallets_qty || 0), 0),
    ctns: notes.reduce((a, n) => a + (n.nop || 0), 0),
    kg: notes.reduce((a, n) => a + (n.weight_kg || 0), 0),
  }), [notes])

  const channels = useMemo(() => {
    const m = new Map<string, { count: number; plts: number; ctns: number }>()
    for (const n of notes) {
      const k = channelOf(n)
      const c = m.get(k) ?? { count: 0, plts: 0, ctns: 0 }
      c.count++; c.plts += n.pallets_qty || 0; c.ctns += n.nop || 0
      m.set(k, c)
    }
    return [...m].sort((a, b) => b[1].count - a[1].count)
  }, [notes])

  const hsTotal = useMemo(() => Object.values(hsBySub).reduce((a, b) => a + b, 0), [hsBySub])
  const customsValue = subs.reduce((a, r) => a + (r.total_customs_value || 0), 0)
  const brokerFees = subs.reduce((a, r) => a + (r.customs_broker_fee || 0), 0)

  const deliveryHint = DELIVERY_CODES.filter(c => existingCodes.has(c)).join(' / ')
  const customsHint = CUSTOMS_CODES.filter(c => existingCodes.has(c)).join(' / ')

  if (!loaded) return null

  return (
    <details open className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
        Billing basis <span className="ml-1 text-xs font-normal text-gray-400">what actually happened on this job · read-only except the postcode cells</span>
      </summary>
      <div className="px-4 pb-4 space-y-5 text-sm">
        <VariablesSection vars={vars} error={varsError} />

        {notes.length > 0 && (
          <section>
            <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-1">📦 Collections / Deliveries</h4>
            <p className="text-gray-600 dark:text-gray-300">
              {notes.length} note{notes.length === 1 ? '' : 's'} · {fmtNum(totals.plts)} plts · {fmtNum(totals.ctns)} ctns
              {totals.kg > 0 && <> · {fmtNum(totals.kg)} kg</>}
              {deliveryHint && <Hint>delivery count: {deliveryHint}</Hint>}
            </p>
            <ul className="mt-2 space-y-0.5 text-gray-700 dark:text-gray-200">
              {channels.map(([name, c]) => (
                <li key={name} className="flex gap-3">
                  <span className="min-w-[10rem]">{name}</span>
                  <span className="font-mono">× {c.count}</span>
                  <span className="text-gray-500">{fmtNum(c.plts)} plts / {fmtNum(c.ctns)} ctns</span>
                </li>
              ))}
            </ul>
            {pcError && <p className="mt-1 text-xs text-red-600">{pcError}</p>}
            <div className="mt-2 overflow-x-auto">
              <table className="min-w-full text-xs">
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {notes.map(n => {
                    const st = statusOf(n)
                    return (
                      <tr key={n.id}>
                        <td className="py-1 pr-3 font-mono whitespace-nowrap">
                          {n.pdf_url
                            ? <button onClick={() => window.open(n.pdf_url!, '_blank')} className="text-blue-600 hover:underline dark:text-blue-400" title="Open the note PDF">#{n.serial_no}</button>
                            : <>#{n.serial_no}</>}
                        </td>
                        <td className="py-1 pr-3 whitespace-nowrap">{channelOf(n)}</td>
                        <td className="py-1 pr-3 text-gray-600 dark:text-gray-300 max-w-[18rem] truncate" title={n.deliver_to_addr || n.deliver_to_name || ''}>
                          {n.deliver_to_name || n.deliver_to_addr || '—'}
                        </td>
                        <td className="py-1 pr-3 whitespace-nowrap font-mono">
                          {editingPc?.id === n.id ? (
                            <input autoFocus value={editingPc.value} placeholder="B76 1AH" size={9}
                              onChange={e => setEditingPc({ id: n.id, value: e.target.value })}
                              onBlur={() => savePostcode(n.id, editingPc.value)}
                              onKeyDown={e => { if (e.key === 'Enter') savePostcode(n.id, editingPc.value); if (e.key === 'Escape') { setEditingPc(null); setPcError(null) } }}
                              className="border border-gray-300 dark:border-gray-600 rounded px-1 py-0.5 text-xs bg-white dark:bg-gray-900 dark:text-white uppercase" />
                          ) : (
                            <button onClick={() => { setPcError(null); setEditingPc({ id: n.id, value: n.delivery_postcode || '' }) }}
                              title="Click to correct the delivery postcode"
                              className={n.delivery_postcode ? 'hover:underline' : 'text-amber-700 dark:text-amber-400 hover:underline'}>
                              {n.delivery_postcode || 'add postcode'}
                            </button>
                          )}
                        </td>
                        <td className="py-1 pr-3 whitespace-nowrap font-mono">{n.pallets_qty ?? 0} plt / {n.nop} ctn</td>
                        <td className={`py-1 whitespace-nowrap ${st.cls}`}>{st.label}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {subs.length > 0 && (
          <section>
            <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-1">🛃 Customs</h4>
            <p className="text-gray-600 dark:text-gray-300">
              {subs.length} declaration{subs.length === 1 ? '' : 's'}
              {hsTotal > 0 && <> · {hsTotal} HS code{hsTotal === 1 ? '' : 's'}</>}
              {customsValue > 0 && <> · customs value {gbp(customsValue)}</>}
              {customsHint && <Hint>declaration count: {customsHint}</Hint>}
            </p>
            {brokerFees > 0 && <p className="text-gray-600 dark:text-gray-300">Broker fee entered: {gbp(brokerFees)}</p>}
            <p className="text-[11px] text-gray-400 mt-0.5">
              Duty and import VAT amounts aren't available yet — HMRC's assessment isn't captured by this system (HMDU / import VAT still come from the customs statement).
            </p>
            <ul className="mt-2 space-y-0.5 text-xs text-gray-700 dark:text-gray-200">
              {subs.map(r => (
                <li key={r.id} className="flex flex-wrap gap-x-3">
                  <Link to={`/jobs/${jobId}/ecustoms`} className="font-mono text-blue-600 hover:underline dark:text-blue-400" title="Open eCustoms for this job">
                    {r.mrn || r.reference || r.id.slice(0, 8)}
                  </Link>
                  <span>{r.submission_type}{r.pva_used != null ? (r.pva_used ? ' · PVA' : ' · non-PVA') : ''}</span>
                  {hsBySub[r.id] != null && <span>{hsBySub[r.id]} HS codes</span>}
                  {r.total_customs_value != null && <span>value {gbp(r.total_customs_value)}</span>}
                  <span className={r.status === 'accepted' ? 'text-green-700 dark:text-green-400' : 'text-gray-500'}>
                    {r.status === 'accepted' ? 'Accepted' : 'Submitted'} {fmtDate(r.declaration_date || r.submitted_at)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </details>
  )
}

const fmtLong = (d?: string | null) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : null

// One variable: its value, or "—" with every missing[] entry that explains it in amber.
function Variable({ label, value, missing }: { label: string; value: React.ReactNode; missing: MissingItem[] }) {
  return (
    <div className="py-1.5 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex gap-3">
        <span className="w-44 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
        <span className={`flex-1 ${value == null ? 'text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>{value ?? '—'}</span>
      </div>
      {missing.length > 0 && (
        <ul className="mt-1 ml-[11.75rem] space-y-0.5 rounded bg-amber-50 dark:bg-amber-900/20 px-2 py-1 text-xs text-amber-800 dark:text-amber-300">
          {missing.map((m, i) => (
            <li key={i}>
              {m.scope !== 'job' && <span className="font-mono mr-1">[{m.scope}]</span>}
              {m.reason} <span className="opacity-75">→ {m.how_to}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function VariablesSection({ vars, error }: { vars: BillingVars | null; error: string | null }) {
  if (error) {
    return (
      <section className="rounded bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300">
        Billing variables unavailable: {error}. (If this says the function does not exist, run migration 20261002b_job_billing_variables.sql.)
      </section>
    )
  }
  if (!vars) return <p className="text-xs text-gray-400">Loading billing variables…</p>
  const m = (...keys: string[]) => vars.missing.filter(x => keys.includes(x.key))
  const storage = vars.storage_days == null ? null : (
    <>
      {vars.storage_days} day{vars.storage_days === 1 ? '' : 's'}
      {vars.storage_days_unreleased != null && <> ({vars.storage_days_unreleased} unreleased / {vars.storage_days_released} released)</>}
      {vars.storage_in_progress && <span className="text-orange-600 dark:text-orange-400"> · still in warehouse, counted to today</span>}
    </>
  )
  return (
    <section>
      <h4 className="font-medium text-gray-800 dark:text-gray-100 mb-1">🧮 Billing variables
        <span className="ml-2 text-[11px] font-normal text-gray-400">amber = not available, fill these in yourself when invoicing</span>
      </h4>
      <Variable label="Billing party" value={vars.billing_party ? `${vars.billing_party.code} – ${vars.billing_party.name}` : null} missing={m('billing_party')} />
      <Variable label="Containers" missing={m('containers', 'container_pallets')}
        value={vars.container_qty == null ? null : (
          <>{vars.container_qty}
            <span className="text-gray-500"> · {vars.containers.map(c => `${c.container_no ?? '?'}${c.container_type ? ` (${c.container_type})` : ''}: ${c.pallets ?? '? '} plt / ${c.cartons ?? '? '} ctn`).join(' · ')}</span>
          </>)} />
      <Variable label="HBLs" value={vars.hbl_qty} missing={m('hbl_qty')} />
      <Variable label="Customs declarations" missing={m('headings', 'headings_by_hbl')}
        value={vars.declaration_qty == null ? null : (
          <>{vars.declaration_qty}<span className="text-gray-500"> · HS items per declaration: {vars.headings.map(h => `${h.declaration ?? '?'}: ${h.hs_count ?? '?'}`).join(', ')}</span></>)} />
      <Variable label="Duty / Import VAT" value={null} missing={m('duty', 'vat')} />
      <Variable label="Deferment account used" value={null} missing={m('deferment_used')} />
      <Variable label="Warehouse in" value={fmtLong(vars.warehouse_in)} missing={m('warehouse_in')} />
      <Variable label="Customs cleared" value={fmtLong(vars.customs_cleared)} missing={m('customs_cleared')} />
      <Variable label="Warehouse out" missing={m('warehouse_out')}
        value={vars.warehouse_out == null ? null : <>{fmtLong(vars.warehouse_out)}{vars.warehouse_out_partial && <span className="text-orange-600 dark:text-orange-400"> · last completed note; some notes still open</span>}</>} />
      <Variable label="Storage" value={storage} missing={m('storage_split')} />
      <Variable label="Dangerous goods" value={vars.is_dg == null ? null : vars.is_dg ? 'Yes' : 'No'} missing={m('is_dg')} />
      <Variable label="Port of discharge" value={vars.pod ? `${vars.pod.name} (${vars.pod.code})` : null} missing={m('pod')} />
      <Variable label="Deliveries" value={vars.deliveries.length ? `${vars.deliveries.length} note${vars.deliveries.length === 1 ? '' : 's'} (listed below)` : null} missing={m('deliveries', 'delivery_method', 'postcode', 'haulier', 'pallets')} />
    </section>
  )
}
